"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.rollback = exports.getRollbackActions = void 0;
const ot_1 = require("../../engine/ot");
const lodash_1 = require("lodash");
const types_1 = require("../../types");
const command_manager_1 = require("../../command_manager");
const utils_1 = require("../../utils");
const selectors_1 = require("../../exports/store/selectors");
const datasheet_1 = require("../../model/datasheet");
const field_1 = require("../../commands/common/field");
const text_field_1 = require("../../model/field/text_field");
const player_1 = require("../../modules/shared/player");
const model_1 = require("../../model");
const getRollbackActions = (operations, state, snapshot) => {
    return operations.reduce((collect, op) => {
        const needIgnoreActionFlag = { field: {}, record: {} };
        // First restore the column-related operations to ensure that the subsequent 
        // action of checking the set cell value can get the correct verification result
        const updateFieldActions = [];
        const addFieldActions = [];
        const otherActions = [];
        op.actions.forEach((action) => {
            const { type } = (0, utils_1.parseAction)(action);
            if ([utils_1.ActionType.UpdateField, utils_1.ActionType.DelField].includes(type)) {
                updateFieldActions.push(action);
            }
            else if (type === utils_1.ActionType.AddField) {
                addFieldActions.push(action);
            }
            else {
                otherActions.push(action);
            }
        });
        const sortedActions = [...updateFieldActions, ...otherActions, ...addFieldActions];
        sortedActions.forEach(item => {
            const action = item;
            if (['comments', 'commentCount'].includes(action.p[2])) {
                return;
            }
            const res = (0, utils_1.parseAction)(action);
            // The original field is link, and the link field will be inserted during recovery. 
            // If the corresponding association table cannot be found, the recovery of this data will be ignored.
            if ([utils_1.ActionType.UpdateField, utils_1.ActionType.DelField].includes(res.type)) {
                const oldField = action.od;
                if (oldField.type === types_1.FieldType.Link && oldField.property.foreignDatasheetId) {
                    const foreDatasheet = (0, selectors_1.getDatasheet)(state, oldField.property.foreignDatasheetId);
                    if (!foreDatasheet) {
                        needIgnoreActionFlag.field[oldField.id] = true;
                        return;
                    }
                }
            }
            // When deleting a column, if the corresponding field has been marked to be ignored, the data will not be restored
            if (res.type === utils_1.ActionType.DelColumn) {
                if (needIgnoreActionFlag.field[res.context.fieldId]) {
                    return;
                }
            }
            // Set the cell value, but the field does not match the type of the value to be set and needs to be ignored
            if (res.type === utils_1.ActionType.UpdateRecord) {
                const fieldId = res.context.fieldId;
                const recordId = res.context.recordId;
                if (needIgnoreActionFlag.field[fieldId] || needIgnoreActionFlag.record[recordId]) {
                    return;
                }
                const oldData = action.od;
                const field = snapshot.meta.fieldMap[fieldId];
                if (!field) {
                    return;
                }
                const validError = model_1.Field.bindContext(field, state).validateCellValue(oldData).error;
                if (validError && !validError.message.endsWith('is required')) {
                    return;
                }
            }
            try {
                const invertActions = ot_1.jot.invert([action]);
                ot_1.jot.apply(snapshot, invertActions);
                collect.push(...invertActions);
            }
            catch (error) {
                player_1.Player.doTrigger(player_1.Events.app_error_logger, {
                    error: new Error(`Time Machine rollback error: ${error}`),
                    metaData: {
                        actions: [action],
                        snapshot,
                    },
                });
            }
            return true;
        });
        return collect;
    }, []);
};
exports.getRollbackActions = getRollbackActions;
exports.rollback = {
    undoable: false,
    execute: (context, options) => {
        const { model: state } = context;
        const { datasheetId, data } = options;
        const { operations } = data;
        const preDatasheet = (0, selectors_1.getDatasheet)(state, datasheetId);
        if (!preDatasheet) {
            return null;
        }
        /* 1. [apply], apply the reverse actions to a cloned snapshot in turn to get the original rollback snapshot */
        const preSnapshot = preDatasheet.snapshot;
        const postSnapshot = (0, utils_1.fastCloneDeep)(preSnapshot);
        const actions = (0, exports.getRollbackActions)(operations, state, postSnapshot);
        console.log(postSnapshot, preSnapshot);
        if (!actions.length) {
            return null;
        }
        // collect actions
        const linkedActions = [];
        function setLinkedActions(datasheetId, actions) {
            if (!actions.length) {
                return;
            }
            const la = linkedActions.find(la => la.datasheetId === datasheetId);
            if (la) {
                la.actions.push(...actions);
            }
            else {
                linkedActions.push({
                    datasheetId,
                    actions,
                });
            }
        }
        /* 2. [diff], compare the snapshot after apply with the previous snapshot,
        take out the changed link field, and generate field changes to the associated table */
        const { deletedLinkFields, newLinkFields, normalLinkFields } = getLinkFieldChange(preSnapshot.meta.fieldMap, postSnapshot.meta.fieldMap);
        console.log({ deletedLinkFields, newLinkFields, normalLinkFields });
        /* 3. [patch], re-establish bidirectional associations for all associated fields and align the data */
        deletedLinkFields.forEach(sourceField => {
            const foreignDatasheetId = sourceField.property.foreignDatasheetId;
            const foreignSnapshot = (0, selectors_1.getSnapshot)(state, foreignDatasheetId);
            const foreignField = (0, selectors_1.getField)(state, sourceField.property.brotherFieldId, foreignDatasheetId);
            const actions = (0, field_1.setField)(context, foreignSnapshot, foreignField, {
                id: foreignField.id,
                name: foreignField.name,
                type: types_1.FieldType.Text,
                property: text_field_1.TextField.defaultProperty(),
            }).actions;
            setLinkedActions(foreignDatasheetId, actions);
        });
        const newForeignField = newLinkFields.map(sourceField => {
            const foreignDatasheetId = sourceField.property.foreignDatasheetId;
            let brotherFieldId = sourceField.property.brotherFieldId;
            const foreignSnapshot = (0, selectors_1.getSnapshot)(state, foreignDatasheetId);
            if (!foreignSnapshot) {
                // one-way association
                return;
            }
            const foreignField = (0, selectors_1.getField)(state, brotherFieldId, foreignDatasheetId);
            const foreignFieldIds = Object.keys(foreignSnapshot.meta.fieldMap);
            /**
             * The brotherFieldId corresponding to newLinkFields still exists in the association table,
             * and the association field is not plain text, you need to modify brotherFieldId to create a new association field
             * Otherwise set the field type of the association table to the magic association type.
             */
            if (foreignField && foreignField.type !== types_1.FieldType.Text) {
                brotherFieldId = (0, utils_1.getNewId)(utils_1.IDPrefix.Field, foreignFieldIds);
                /** A deep clone avoids the following DatasheetActions.setFieldAttr2Action method to get the same value for ac oi and od.
                 * In order to avoid the occurrence of od when the middle layer does OT conversion,
                 * the value corresponding to the associated table fieldMap cannot be found
                 */
                sourceField = (0, lodash_1.cloneDeep)(sourceField);
                sourceField.property.brotherFieldId = brotherFieldId;
                const ac = datasheet_1.DatasheetActions.setFieldAttr2Action(postSnapshot, {
                    field: sourceField
                });
                if (ac) {
                    actions.push(ac);
                    ot_1.jot.apply(postSnapshot, [ac]);
                }
                const newField = {
                    id: brotherFieldId,
                    type: types_1.FieldType.Link,
                    name: (0, utils_1.getUniqName)(preDatasheet.name, foreignFieldIds.map(id => foreignSnapshot.meta.fieldMap[id].name)),
                    property: {
                        foreignDatasheetId: datasheetId,
                        brotherFieldId: sourceField.id,
                    }
                };
                const newFieldActions = (0, field_1.createNewField)(foreignSnapshot, newField);
                setLinkedActions(foreignDatasheetId, newFieldActions);
                return newField;
            }
            const modifiedField = Object.assign(Object.assign({}, foreignField), { type: types_1.FieldType.Link, property: {
                    foreignDatasheetId: datasheetId,
                    brotherFieldId: sourceField.id,
                } });
            const { actions: modifiedFieldActions } = (0, field_1.setField)(context, foreignSnapshot, foreignField, modifiedField);
            setLinkedActions(foreignDatasheetId, modifiedFieldActions);
            return sourceField;
        });
        newLinkFields.forEach((sourceField, index) => {
            console.log('newLinkFields: ', sourceField);
            if (!newForeignField[index]) {
                // one-way direction
                return;
            }
            const result = patchFieldValues(state, postSnapshot, sourceField, newForeignField[index]);
            actions.push(...result.sourceActions);
            setLinkedActions(sourceField.property.foreignDatasheetId, result.foreignActions);
        });
        normalLinkFields.forEach(sourceField => {
            const result = patchFieldValues(state, postSnapshot, sourceField);
            actions.push(...result.sourceActions);
            setLinkedActions(sourceField.property.foreignDatasheetId, result.foreignActions);
        });
        return {
            result: command_manager_1.ExecuteResult.Success,
            resourceId: datasheetId,
            resourceType: types_1.ResourceType.Datasheet,
            actions,
            linkedActions,
        };
    },
};
/**
 * Get an array of new and deleted associated fields
 * @param preFieldMap fieldMap before rollback
 * @param postFieldMap rollback fieldMap
 * @return deletedLinkFields Link fields deleted after rollback
 * @return newLinkFields added after rollback
 * @return normalLinkFields rollback existing associated fields
 */
function getLinkFieldChange(preFieldMap, postFieldMap) {
    let deletedLinkFields = [];
    let newLinkFields = [];
    let normalLinkFields = [];
    /**
      * The three cases are regarded as deletedLinkFields. After preFieldMap gets a field, it is matched with fieldId in postFieldMap
      * pre is linkField, this field cannot be matched in post
      * pre is a linkField, and it is not a link field that is converted in post
      * pre is a linkField, the post is converted into another link field, or the sibling fields are inconsistent,
      * (the link field in the post is considered new at this time)
      *
      * Three cases are treated as newLinkFields
      * post is a linkField, this field does not exist in pre
      * post is a linkField, pre is not a linkField
      * pre is a linkField, the post is converted into another link field, or the sibling fields are inconsistent, (same as the third item above)
      *
      * One case is treated as normalLinkFields
      * exists in both pre and post, and the associated table and sibling fields have not changed
      */
    Object.values(preFieldMap).forEach(preField => {
        const postField = postFieldMap[preField.id];
        // post is linkField, pre is not linkField, you need to push a record to newLinkFields;
        if (postField && postField.type === types_1.FieldType.Link) {
            if (preField && preField.type !== types_1.FieldType.Link) {
                newLinkFields.push(postField);
                return;
            }
        }
        // pre is not a linkField to filter out first
        if (preField.type !== types_1.FieldType.Link) {
            return;
        }
        // This field cannot be matched in the post
        if (!postField) {
            deletedLinkFields.push(preField);
            return;
        }
        // The post is converted not the link field
        if (postField.type !== types_1.FieldType.Link) {
            deletedLinkFields.push(preField);
            return;
        }
        // Posts are not related to the same table
        if (preField.property.foreignDatasheetId !== postField.property.foreignDatasheetId) {
            deletedLinkFields.push(preField);
            newLinkFields.push(postField);
            return;
        }
        // The post is associated with the same table, but the sibling fields are not the same
        if (preField.property.brotherFieldId !== postField.property.brotherFieldId) {
            deletedLinkFields.push(preField);
            newLinkFields.push(postField);
            return;
        }
        // Both pre and post exist, and the associated table and sibling fields have not changed
        normalLinkFields.push(postField);
    });
    // post is a linkField, this field does not exist in pre
    const newInPostFields = Object.values(postFieldMap).filter(postField => !preFieldMap[postField.id] && postField.type === types_1.FieldType.Link);
    newLinkFields.push(...newInPostFields);
    // Filter out the fields where brotherFieldId is empty, that is, the fields associated with the table
    deletedLinkFields = deletedLinkFields.filter(field => field.property.brotherFieldId);
    newLinkFields = newLinkFields.filter(field => field.property.brotherFieldId);
    normalLinkFields = normalLinkFields.filter(field => field.property.brotherFieldId);
    return { deletedLinkFields, newLinkFields, normalLinkFields };
}
// Provide sourceLinkField to align the cellValue in the field corresponding to the association table to a bidirectional association
function patchFieldValues(state, sourceSnapshot, sourceField, 
// The newly added field of the association table is not in the snapshot, and needs to be added in
isolateForeignField) {
    const foreignDatasheetId = sourceField.property.foreignDatasheetId;
    const foreignSnapshot = (0, selectors_1.getSnapshot)(state, foreignDatasheetId);
    const foreignField = isolateForeignField || (0, selectors_1.getField)(state, sourceField.property.brotherFieldId, foreignDatasheetId);
    // Generate the value required by the sibling field
    const foreignLinkRecordValueMap = {};
    const foreignActions = [];
    const sourceActions = [];
    function eachFieldValue(fieldId, recordMap, cb) {
        for (const recordId in recordMap) {
            const record = recordMap[recordId];
            const linkCellValue = record.data && record.data[fieldId];
            cb(recordId, linkCellValue);
        }
    }
    // Generate a value map of the associated column based on postField
    eachFieldValue(sourceField.id, sourceSnapshot.recordMap, (recordId, linkCellValue) => {
        if (!linkCellValue) {
            return;
        }
        const filteredLinkCellValue = linkCellValue.filter(rid => {
            // If the associated cell summary rid does not exist in the associated table, delete this rid
            if (!foreignSnapshot.recordMap[rid]) {
                return false;
            }
            if (foreignLinkRecordValueMap[rid]) {
                foreignLinkRecordValueMap[rid].push(recordId);
            }
            else {
                foreignLinkRecordValueMap[rid] = [recordId];
            }
            return true;
        });
        if (filteredLinkCellValue.length !== linkCellValue.length) {
            const action = datasheet_1.DatasheetActions.setRecord2Action(sourceSnapshot, {
                fieldId: sourceField.id,
                recordId: recordId,
                value: filteredLinkCellValue,
            });
            action && sourceActions.push(action);
        }
    });
    console.log({ foreignLinkRecordValueMap });
    // Traverse the association table, replace the existing ones, delete the ones that don't exist, and add the missing ones
    foreignSnapshot && eachFieldValue(foreignField.id, foreignSnapshot.recordMap, (recordId, linkCellValue) => {
        let action = null;
        const newCellValue = foreignLinkRecordValueMap[recordId];
        if (newCellValue) {
            // need to be replaced if there are different values in the array
            if ((0, lodash_1.xor)(linkCellValue, newCellValue)) {
                action = datasheet_1.DatasheetActions.setRecord2Action(foreignSnapshot, {
                    fieldId: foreignField.id,
                    recordId: recordId,
                    value: newCellValue,
                });
            }
        }
        else {
            action = datasheet_1.DatasheetActions.setRecord2Action(foreignSnapshot, {
                fieldId: foreignField.id,
                recordId: recordId,
                value: null,
            });
        }
        action && foreignActions.push(action);
    });
    console.log({ sourceActions, foreignActions });
    return { sourceActions, foreignActions };
}
//# sourceMappingURL=rollback.js.map